/**
 * The builder's typed turn-failure codes, as the webapp classifies them. The
 * server's set is `BUILDER_STREAM_ERROR_CODES` (`stream-errors.ts`); the
 * client adds its own `STREAM_TIMEOUT` (the stall watchdog). One module so the
 * failure card, the hook's retry affordance and the toast never disagree;
 * `tests/architecture/builder-client-mirrors-parity.test.ts` pins this file
 * against the server's list.
 */

/**
 * The step ran out of TIME: a stalled stream or a provider timeout — the one
 * failure shape a smaller step fixes, so only these offer "Simplify this step".
 * A billing stop or an internal error is not a size problem; offering to split
 * the plan there told a user his step was "too large" when the platform had
 * refused to start the turn at all (ATS session, 2026-10-06).
 */
export const STEP_TOO_LARGE_FAILURE_CODES: ReadonlySet<string> = new Set([
  'STREAM_TIMEOUT',
  'PROVIDER_TIMEOUT',
]);

/**
 * Failures caused by credits — worded as "stopped because of credits", not
 * "could not start": the daily burn cap can trip mid-turn after files were
 * written.
 */
export const BILLING_STOP_FAILURE_CODES: ReadonlySet<string> = new Set([
  'INSUFFICIENT_CREDITS',
  'DAILY_BURN_CAP_EXCEEDED',
]);

/** Safe to retry the identical turn unchanged — lights the inline Retry. */
export const RETRYABLE_FAILURE_CODES: ReadonlySet<string> = new Set([
  'PROVIDER_TIMEOUT',
  'PROVIDER_OVERLOADED',
  'STREAM_TIMEOUT',
]);

/** Every typed code the client knows how to word (parity target). */
export const KNOWN_FAILURE_CODES: ReadonlySet<string> = new Set([
  ...STEP_TOO_LARGE_FAILURE_CODES,
  ...BILLING_STOP_FAILURE_CODES,
  ...RETRYABLE_FAILURE_CODES,
]);
